import { Button, TextLink, Typography } from '@neo4j-ndl/react';
import { isNotNullish } from '@nx/stdlib';
import cn from 'classnames';
import type { ReactNode } from 'react';

import './section.css';

type SectionProps = {
  title: ReactNode;
  description: string;
  buttonOptions?: { text: string; action: () => void; animated?: boolean };
  highlighted: boolean;
  link?: { text: string; href: string; external: boolean; onClick?: () => void };
  extra?: ReactNode;
};

export const Section = ({ title, description, highlighted, buttonOptions, link, extra }: SectionProps) => {
  const titleClasses = cn('flex items-center gap-2', {
    'text-neutral-text-default': highlighted,
    'text-neutral-text-weaker': !highlighted,
  });
  const descriptionClasses = cn('flex flex-col gap-6', {
    'text-neutral-text-weak': highlighted,
    'text-neutral-text-weaker': !highlighted,
  });
  const buttonClasses = cn('relative w-fit', {
    'animated-button text-primary-text !border-0': buttonOptions?.animated ?? false,
    'text-neutral-text-weak': !highlighted,
  });

  return (
    <div className="mb-[84px] flex flex-col gap-4">
      <div className={titleClasses}>{title}</div>
      <div className={descriptionClasses}>
        <Typography variant="body-medium" className="text-neutral-text-weak">
          {description}
        </Typography>
        {isNotNullish(buttonOptions) && (
          <div className="flex items-center gap-4">
            <Button onClick={buttonOptions.action} fill="outlined" className={buttonClasses} color="neutral">
              {buttonOptions.text}
            </Button>
            {isNotNullish(link) && (
              <Typography variant="body-medium">
                <TextLink
                  href={link.href}
                  {...(link.external && { type: 'external' })}
                  htmlAttributes={{ onClick: link.onClick }}
                >
                  {link.text}
                </TextLink>
              </Typography>
            )}
          </div>
        )}
      </div>
      {extra}
    </div>
  );
};
